/**
 * Full-page wizard layout.
 * No sidebar, no main app nav — only a minimal header and full viewport for the wizard.
 * Use for flows that should feel like a standalone page (e.g. broker registration wizard).
 */

import { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

interface FullPageWizardLayoutProps {
  children: ReactNode;
  /** Short title shown in the header (e.g. "Connect Zerodha") */
  title?: string;
  /** Back button label */
  backLabel?: string;
  /** Back navigation path */
  backTo?: string;
  /** Optional back handler; if not set, uses backTo with navigate() */
  onBack?: () => void;
}

export function FullPageWizardLayout({
  children,
  title,
  backLabel = 'Back to broker selection',
  backTo = '/broker-accounts/add',
  onBack,
}: FullPageWizardLayoutProps) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        color: '#04377b',
      }}
    >
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
          py: 2,
          px: { xs: 2, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Box
          component={Link}
          to="/broker-accounts"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src="/assets/flowrithm-logo.png"
            alt="FlowRithm"
            sx={{ height: 32 }}
          />
        </Box>
        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', height: 24, mx: 1 }} />
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack ?? (() => navigate(backTo))}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(4, 55, 123, 0.04)',
              color: '#04377b',
            }
          }}
        >
          {backLabel}
        </Button>
        {title && (
          <Typography
            variant="h6"
            sx={{
              ml: 'auto',
              color: '#1a1a1a',
              fontSize: '1.125rem',
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </Typography>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backgroundImage: 'url(/assets/login-hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.78) 45%, rgba(255,255,255,0.65) 65%)',
            zIndex: 0,
          },
          overflow: 'auto',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
